import React, { createContext, useState, useEffect, useContext } from "react";
import SuccessSvg from './svg/Success.svg'
import './style.scss'
// 创建Context用于跨组件通信
const ToastContext = createContext();


// Toast容器组件
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    
    // 添加Toast
    const addToast = (message, options = {}) => {
      const id = Date.now();
      const newToast = {
        id,
        message,
        type: options.type || 'info',
        duration: options.duration || 3000,
        position: options.position || 'top-center',
        ...options
      };
      
      setToasts(prevToasts => [...prevToasts, newToast]);
      
      // 如果设置了自动关闭，设置定时器
      if (newToast.duration !== 0) {
        setTimeout(() => {
          removeToast(id);
        }, newToast.duration);
      }
      
      return id;
    };
    
    // 移除Toast
    const removeToast = (id) => {
      setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    };
    
    // 提供快捷方法
    const toast = {
      success: (message, options) => addToast(message, { ...options, type: 'success' }),
      error: (message, options) => addToast(message, { ...options, type: 'error' }),
      warning: (message, options) => addToast(message, { ...options, type: 'warning' }),
      info: (message, options) => addToast(message, { ...options, type: 'info' }),
      remove: removeToast
    };
    
    return (
      <ToastContext.Provider value={toast}>
        {children}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </ToastContext.Provider>
    );
  };

  // Toast容器
const ToastContainer = ({ toasts, removeToast }) => {
    // 按位置分组toasts
    let theme = null
    const groupedToasts = toasts.reduce((groups, toast) => {
      const position = toast.position;
      if(toast.theme)
        theme = toast.theme
      if (!groups[position]) groups[position] = [];
      groups[position].push(toast);
      return groups;
    }, {});
    
    return (
      <>
        {Object.entries(groupedToasts).map(([position, positionToasts]) => (
          <div key={position} className={`toast-container ${position} ${theme?theme:''}`}>
            {positionToasts.map(toast => (
              <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
            ))}
          </div>
        ))}
      </>
    );
  };

// 单个Toast组件
const ToastItem = ({ toast, removeToast }) => {
  const [isExiting, setIsExiting] = useState(false);
  
  useEffect(() => {
    // 入场动画
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const element = document.getElementById(`toast-${toast.id}`);
        if (element) {
          element.classList.add('enter');
        }
      });
    });
  }, []);
  
  const handleClose = () => {
    setIsExiting(true);
    // 等待动画完成后再移除
    setTimeout(() => {
      removeToast(toast.id);
    }, 300);
  };
  
  // 自动关闭
  useEffect(() => {
    if (toast.duration === 0) return;
    
    const timer = setTimeout(() => {
      handleClose();
    }, toast.duration);
    
    return () => clearTimeout(timer);
  }, [toast.duration, toast.id]);
  
  // 获取图标
  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <SuccessSvg></SuccessSvg>;
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': 
      default: 
        return 'ℹ';
    }
  };
  
  return (
    <div 
      id={`toast-${toast.id}`}
      className={`toast  ${isExiting ? 'exit' : ''} ${toast.theme?toast.theme:''}`}
    >
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-content">
        <div className="toast-message">{toast.message}</div>
      </div>
    </div>
  );
};

// 使用Toast的hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;